import { ReactElement } from 'react';
import MappingRuleResource from '../../Entities/MappingRules';
import { useRecordContext, useRefresh } from 'react-admin';
import { Grid2 as Grid, LinearProgress } from '@mui/material';
import SelfPaginatingBulkAction from '../../Components/SelfPaginatingBulkAction';
import { idFrom } from '../../utils';

export default function ActionPanel(): ReactElement {
  const record = useRecordContext<MappingRuleResource>();
  const refresh = useRefresh();

  if (!record) {
    return <LinearProgress />;
  }

  return (
    <Grid container spacing={2}>
      <Grid container size={{ xs: 12, xl: 3 }} spacing={2}>
        <Grid size={12}>
          <SelfPaginatingBulkAction
            buttonLabel={(total) => `Approve ${total} offers`}
            changes={{ attributes: { is_approved: true } }}
            disabled={idFrom(record, 'merchant') === undefined}
            filter={{
              equals: {
                merchant: { id: idFrom(record, 'merchant') },
                external_category: record.attributes.external_category,
                is_approved: false,
              },
            }}
            refresh={refresh}
            resource="offers"
          />
        </Grid>
        <Grid size={12}>
          <SelfPaginatingBulkAction
            buttonLabel={(total) => `Assign ${total} unassigned offers to category`}
            changes={{
              relationships: {
                category: {
                  data: idFrom(record, 'category')
                    ? { id: idFrom(record, 'category'), type: 'categories' }
                    : null,
                },
              },
            }}
            disabled={idFrom(record, 'category') === undefined}
            filter={{
              equals: {
                merchant: { id: idFrom(record, 'merchant') },
                external_category: record.attributes.external_category,
              },
              isNull: { category: true },
            }}
            resource="offers"
            refresh={refresh}
          />
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12, xl: 3 }} spacing={2}>
        <Grid size={12}>
          <SelfPaginatingBulkAction
            buttonLabel={(total) => `Reassign ${total} offers to category`}
            disabled={idFrom(record, 'category') === undefined}
            changes={{
              relationships: {
                category: {
                  data: {
                    id: idFrom(record, 'category'),
                    type: 'categories',
                  },
                },
              },
            }}
            color="warning"
            filter={{
              equals: {
                merchant: { id: idFrom(record, 'merchant') },
                external_category: record.attributes.external_category,
              },
              and: {
                not: {
                  isNull: { category: true },
                  equals: {
                    category: {
                      id: idFrom(record, 'category') ?? '0',
                    },
                  },
                },
              },
            }}
            refresh={refresh}
            resource="offers"
          />
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12, xl: 3 }} spacing={2}>
        <Grid size={12}>
          <SelfPaginatingBulkAction
            buttonLabel={(total) => `Unapprove ${total} offers`}
            changes={{ attributes: { is_approved: false } }}
            color="error"
            filter={{
              equals: {
                merchant: { id: idFrom(record, 'merchant') },
                external_category: record.attributes.external_category,
                is_approved: true,
              },
            }}
            refresh={refresh}
            resource="offers"
          />
        </Grid>
        <Grid size={12}>
          <SelfPaginatingBulkAction
            buttonLabel={(total) => `Unassign ${total} offers from category`}
            color="error"
            resource="offers"
            refresh={refresh}
            disabled={idFrom(record, 'category') === undefined}
            filter={{
              equals: {
                category: { id: idFrom(record, 'category') },
                merchant: { id: idFrom(record, 'merchant') },
                external_category: record.attributes.external_category,
              },
            }}
            changes={{ relationships: { category: { data: null } } }}
          />
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12, xl: 3 }} spacing={2}>
        <Grid size={12}>
          <SelfPaginatingBulkAction
            buttonLabel={(total) => `Delete ${total} offers matching rule`}
            changeType="delete"
            color="error"
            confirm={(total) =>
              `Are you sure you want to delete the ${total} offers that` +
              ' match this rule? These offers will be deleted ' +
              (record.attributes.ignored
                ? 'and cannot be recovered'
                : 'but may be re-ingested in the next Scan')
            }
            disabled={idFrom(record, 'merchant') === undefined}
            filter={{
              equals: {
                merchant: { id: idFrom(record, 'merchant') },
                external_category: record.attributes.external_category,
              },
            }}
            refresh={refresh}
            resource="offers"
          />
        </Grid>
      </Grid>
    </Grid>
  );
}
